import {
  hashReportHeatmapDescriptor,
  renderReportHeatmapBundle,
  type ReportHeatmapBundle,
  type ReportHeatmapCompositeDescriptor,
  type ReportHeatmapDescriptor,
  type ReportHeatmapRequest,
} from '@asset-viz/reporting/reportHeatmap'
import type { GeckoComponentRect } from 'types'

import {
  composeReportHeatmapBlobs,
  getReportHeatmapSlices,
  type ReportHeatmapSliceAxis,
} from './reportHeatmapSlices'

interface RenderReportHeatmapSlicesOptions {
  axis?: ReportHeatmapSliceAxis
  sharedSliceBounds?: GeckoComponentRect[]
}

export const createReportHeatmapCompositeMetadata = (
  axis: 'x' | 'y',
  sliceDescriptors: Array<Pick<ReportHeatmapDescriptor, 'bounds'>>
): ReportHeatmapCompositeDescriptor => ({
  axis,
  layout: axis === 'x' ? 'stacked' : 'side-by-side',
  slices: sliceDescriptors.map((slice, index) => ({
    index,
    bounds: slice.bounds,
  })),
})

export const renderReportHeatmapSlices = async (
  request: ReportHeatmapRequest,
  options: RenderReportHeatmapSlicesOptions = {}
): Promise<ReportHeatmapBundle> => {
  const sharedSliceCount = options.sharedSliceBounds?.length
    ? Math.max(
        ...options.sharedSliceBounds.map(
          (bounds) => getReportHeatmapSlices(bounds, options).length
        )
      )
    : undefined
  const slices = getReportHeatmapSlices(request.bounds, {
    ...options,
    sliceCount: sharedSliceCount,
  })
  const bundles = await Promise.all(
    slices.map((slice) =>
      renderReportHeatmapBundle({
        ...request,
        bounds: slice.bounds,
        includeSectionLabels:
          request.includeSectionLabels !== false &&
          (slices.length === 1 || slice.index === slices.length - 1),
        sectionLabelBounds: request.bounds,
      })
    )
  )

  if (bundles.length === 1 || slices[0].axis === undefined) {
    return bundles[0]
  }

  const axis = slices[0].axis
  const descriptorWithoutHash: Omit<ReportHeatmapDescriptor, 'hash'> = {
    ...bundles[0].descriptor,
    composite: createReportHeatmapCompositeMetadata(
      axis,
      bundles.map((bundle) => bundle.descriptor)
    ),
  }

  return {
    heatmapBlob: await composeReportHeatmapBlobs(
      axis,
      bundles.map((bundle) => bundle.heatmapBlob)
    ),
    legendBlob: bundles[0].legendBlob,
    descriptor: {
      ...descriptorWithoutHash,
      hash: hashReportHeatmapDescriptor(descriptorWithoutHash),
    },
  }
}
